import {
  Cancel as CancelIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Pause as PauseIcon,
  Payment as PaymentIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import { Box, Button, Chip, CircularProgress, Divider, IconButton, Stack, TextField, Typography } from '@mui/material';
import numeral from 'numeral';
import { useState } from 'react';
import { fDate } from '../../../../utils/formatTime';
import {
  Campaign,
  useCampaignAction,
  useInitiatePayment,
  useUpdateAdUrl,
} from '../hooks/useCampaigns';
import { translateKey } from 'src/locales/translate';


interface CampaignMetrics {
  impressions: number;
  clicks: number;
  conversions: number;
  cost: number;
  ctr: number;
  averageCpc: number;
  lastSyncedAt: Date | null;
}

interface CampaignSettingsPanelProps {
  campaign?: Campaign;
  organizationId: string;
  onUpdated: () => void;
  onDeleted?: () => void;
}

const STATUS_COLORS: Record<
  string,
  'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'
> = {
  DRAFT: 'default',
  PENDING_PAYMENT: 'warning',
  ACTIVE: 'success',
  PAUSED: 'warning',
  COMPLETED: 'primary',
  CANCELLED: 'error',
};

const STATUS_LABELS: Record<string, string> = {
  DRAFT: translateKey("visit.formStatusDraft"),
  PENDING_PAYMENT: translateKey("hardcoded.oczekuje_na_platnosc"),
  ACTIVE: 'Aktywna',
  PAUSED: 'Wstrzymana',
  COMPLETED: translateKey("visit.calendarFilterStatusCompleted"),
  CANCELLED: translateKey("visit.calendarFilterStatusCancelled"),
};

export default function CampaignSettingsPanel({
  campaign,
  organizationId,
  onUpdated,
  onDeleted,
}: CampaignSettingsPanelProps) {
  const {
    pause,
    resume,
    cancel,
    delete: deleteCampaign,
    isPausing,
    isResuming,
    isCancelling,
    isDeleting,
  } = useCampaignAction(campaign?.id || '', organizationId);

  const { mutate: initiatePayment, isPending: isInitiatingPayment } =
    useInitiatePayment(campaign?.id || '');

  const { mutate: updateAdUrl, isPending: isUpdatingAdUrl } =
    useUpdateAdUrl(campaign?.id || '', organizationId);

  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const [adUrlValue, setAdUrlValue] = useState('');

  const handleEditUrl = (): void => {
    setAdUrlValue(campaign?.googleCampaignId ? `https://app.vetsi.pl/placowka/` : '');
    setIsEditingUrl(true);
  };

  const handleSaveUrl = (): void => {
    if (!adUrlValue.trim()) return;
    updateAdUrl(adUrlValue.trim(), {
      onSuccess: () => {
        setIsEditingUrl(false);
        onUpdated();
      },
    });
  };

  const handleCancelEditUrl = (): void => {
    setIsEditingUrl(false);
    setAdUrlValue('');
  };

  const handlePayment = () => {
    initiatePayment(undefined, {
      onSuccess: (data) => {
        if (data.paymentUrl) {
          window.location.replace(data.paymentUrl);
        }
      },
    });
  };

  const handlePause = () => {
    pause(undefined, { onSuccess: onUpdated });
  };

  const handleResume = () => {
    resume(undefined, { onSuccess: onUpdated });
  };

  const handleCancel = () => {
    cancel(undefined, { onSuccess: onUpdated });
  };

  const handleDelete = () => {
    deleteCampaign(undefined, { onSuccess: onDeleted ?? onUpdated });
  };

  if (!campaign) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">{translateKey("hardcoded.wybierz_kampanie_z_listy_aby_zobaczyc_szczegoly")}</Typography>
      </Box>
    );
  }

  const metrics = campaign.metrics as unknown as CampaignMetrics;

  return (
    <Stack sx={{ height: '100%', justifyContent: 'space-between' }}>
      <Stack spacing={0.5} sx={{ mb: 2 }}>
        <Chip
          size="small"
          label={STATUS_LABELS[campaign.status] || campaign.status}
          color={STATUS_COLORS[campaign.status] || 'default'}
          sx={{ alignSelf: 'flex-start' }}
        />
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <Stack spacing={2} sx={{ flex: 1 }}>
        {campaign.description && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary">{translateKey("search.ui.aboutTab")}</Typography>
            <Typography>{campaign.description}</Typography>
          </Box>
        )}

        <Box>
          <Typography variant="subtitle2" color="text.secondary">{translateKey("hardcoded.budzet_dzienny")}</Typography>
          <Typography>
            {numeral(campaign.dailyBudget / 100).format('0,0')} PLN
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" color="text.secondary">{translateKey("hardcoded.budzet_calkowity")}</Typography>
          <Typography>
            {numeral(campaign.totalBudget / 100).format('0,0')} PLN
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Okres trwania
          </Typography>
          <Typography>
            {fDate(campaign.startDate)} - {fDate(campaign.endDate)}
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" color="text.secondary">{translateKey("hardcoded.slowa_kluczowe")}{campaign.keywords.length})
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {campaign.keywords.map((keyword: string, index: number) => (
              <Chip
                key={index}
                label={keyword}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>

        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Strategia licytacji
          </Typography>
          <Typography>{campaign.biddingStrategy.replace(/_/g, ' ')}</Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Lokalizacje docelowe
          </Typography>
          <Typography>{campaign.targetLocations.join(', ')}</Typography>
        </Box>

        {(campaign.status === 'ACTIVE' || campaign.status === 'PAUSED') &&
          campaign.googleCampaignId && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              URL reklamy
            </Typography>
            {isEditingUrl ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  fullWidth
                  value={adUrlValue}
                  onChange={(e) => setAdUrlValue(e.target.value)}
                  placeholder="https://app.vetsi.pl/placowka/..."
                  disabled={isUpdatingAdUrl}
                />
                <IconButton
                  size="small"
                  color="success"
                  onClick={handleSaveUrl}
                  disabled={isUpdatingAdUrl || !adUrlValue.trim()}
                >
                  {isUpdatingAdUrl ? <CircularProgress size={18} /> : <CheckIcon fontSize="small" />}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleCancelEditUrl}
                  disabled={isUpdatingAdUrl}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            ) : (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="body2" color="text.secondary">{translateKey("hardcoded.zmien_url_docelowy_reklamy")}</Typography>
                <IconButton size="small" onClick={handleEditUrl}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
          </Box>
        )}

        {metrics?.lastSyncedAt && (
          <>
            <Divider />
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                Statystyki Google Ads
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">{translateKey("hardcoded.wyswietlenia")}</Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {numeral(metrics.impressions).format('0,0')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{translateKey("hardcoded.klikniecia")}</Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {numeral(metrics.clicks).format('0,0')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Konwersje
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {numeral(metrics.conversions).format('0,0')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Wydatki
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {numeral(metrics.cost / 100).format('0,0')} PLN
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    CTR
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {numeral(metrics.ctr * 100).format('0.00')}%
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{translateKey("hardcoded.sr_cpc")}</Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {numeral(metrics.averageCpc / 100).format('0,0.00')} PLN
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Stack>

      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        {(campaign.status === 'ACTIVE' || campaign.status === 'PAUSED') && (
          <Button
            variant="contained"
            color="error"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            disabled={isCancelling}
          >{translateKey("common.cancel")}</Button>
        )}
        {(campaign.status === 'DRAFT' ||
          campaign.status === 'CANCELLED' ||
          campaign.status === 'COMPLETED') && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={isDeleting}
          >{translateKey("common.remove")}</Button>
        )}
        {campaign.status === 'PENDING_PAYMENT' && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<PaymentIcon />}
            onClick={handlePayment}
            disabled={isInitiatingPayment}
          >{translateKey("hardcoded.zaplac")}</Button>
        )}
        {campaign.status === 'ACTIVE' && (
          <Button
            variant="contained"
            color="warning"
            startIcon={<PauseIcon />}
            onClick={handlePause}
            disabled={isPausing}
          >
            Wstrzymaj
          </Button>
        )}
        {campaign.status === 'PAUSED' && (
          <Button
            variant="contained"
            color="success"
            startIcon={<PlayIcon />}
            onClick={handleResume}
            disabled={isResuming}
          >{translateKey("hardcoded.wznow")}</Button>
        )}
      </Stack>
    </Stack>
  );
}
